import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useD3ScatterPlot from './useD3ScatterPlot';
import DeputadoTooltip from './DeputadoTooltip';
import { 
  deputadosData, 
  partidos, 
  estados, 
  getDeputadosByPartido, 
  getDeputadosByEstado, 
  searchDeputados 
} from './mockData';
import './ScatterPlot.css';

/**
 * ScatterPlot component displays a scatter plot of deputies with performance metrics
 * 
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS class
 * @returns {JSX.Element} - Rendered component
 */
const ScatterPlot = ({ className = '' }) => {
  // State for filters
  const [selectedPartido, setSelectedPartido] = useState('TODOS');
  const [selectedEstado, setSelectedEstado] = useState('TODOS');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState(deputadosData);
  
  // State for tooltip
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Apply filters when they change
  useEffect(() => {
    let result = [...deputadosData];
    
    // Apply partido filter
    if (selectedPartido !== 'TODOS') {
      result = getDeputadosByPartido(selectedPartido);
    }
    
    // Apply estado filter
    if (selectedEstado !== 'TODOS') {
      result = result.filter(dep => getDeputadosByEstado(selectedEstado).includes(dep));
    }
    
    // Apply search filter
    if (searchQuery.trim() !== '') {
      result = result.filter(dep => searchDeputados(searchQuery).includes(dep));
    }
    
    setFilteredData(result);
  }, [selectedPartido, selectedEstado, searchQuery]);
  
  // Options for the scatter plot
  const chartOptions = {
    width: 1200,
    height: 800,
    margin: { top: 100, right: 80, bottom: 100, left: 100 },
    backgroundColor: '#6366f1', // Indigo background color
    gridColor: 'rgba(255, 255, 255, 0.2)',
    textColor: '#ffffff',
    pointRadius: 4,
    hexbinRadius: 12,
    hexbinOpacity: 0.7,
    duration: 750,
    showPartyLabels: true,
    highlightedParty: selectedPartido !== 'TODOS' ? selectedPartido : null
  };
  
  // Callbacks for point hover
  const handlePointHover = (deputado, position) => {
    setTooltipData(deputado);
    
    // Position the tooltip in the center of the screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    setTooltipPosition({
      x: viewportWidth / 2, // Center horizontally
      y: viewportHeight / 2 // Center vertically
    });
    
    setTooltipVisible(true);
  };
  
  const handlePointLeave = () => {
    // We'll keep the tooltip visible until the user clicks anywhere
  };
  
  // Close tooltip when clicking anywhere
  const handleCloseTooltip = () => {
    setTooltipVisible(false);
  };
  
  // Add event listener for clicks to close the tooltip
  useEffect(() => {
    if (tooltipVisible) {
      document.addEventListener('click', handleCloseTooltip);
      
      return () => {
        document.removeEventListener('click', handleCloseTooltip);
      };
    }
  }, [tooltipVisible]);
  
  // Use the custom hook to create the scatter plot
  const svgRef = useD3ScatterPlot(
    filteredData,
    chartOptions,
    handlePointHover,
    handlePointLeave
  );
  
  // Handle filter changes
  const handlePartidoChange = (e) => {
    setSelectedPartido(e.target.value);
  };
  
  const handleEstadoChange = (e) => {
    setSelectedEstado(e.target.value);
  };
  
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  const handleRemoveFilters = () => {
    setSelectedPartido('TODOS');
    setSelectedEstado('TODOS');
    setSearchQuery('');
  };
  
  return (
    <div className={`scatter-plot-container ${className}`}>
      {/* Filters */}
      <div className="scatter-plot-filters">
        <button 
          className="filter-remove-btn"
          onClick={handleRemoveFilters}
        >
          <span className="filter-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H21M10 12H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </span>
          REMOVER
          <span className="close-icon">×</span>
        </button>
        
        <div className="filter-dropdown partido-dropdown">
          <select 
            value={selectedPartido}
            onChange={handlePartidoChange}
            className="partido-select"
          >
            <option value="TODOS">PARTIDOS</option>
            {partidos.map(partido => (
              <option key={partido} value={partido}>
                {partido}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-search">
          <input 
            type="text"
            placeholder="BUSCAR DEPUTADO"
            value={searchQuery}
            onChange={handleSearchChange}
            className="deputy-search-input"
          />
          <span className="search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
        
        <div className="filter-dropdown estado-dropdown">
          <select 
            value={selectedEstado}
            onChange={handleEstadoChange}
            className="estado-select"
          >
            <option value="TODOS">ESTADO</option>
            {estados.filter(estado => estado !== 'TODOS').map(estado => (
              <option key={estado} value={estado}>
                {estado}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Scatter Plot */}
      <div className="scatter-plot-chart">
        <svg ref={svgRef} className="scatter-plot-svg"></svg>
      </div>
      
      {/* Tooltip */}
      <DeputadoTooltip 
        deputado={tooltipData}
        visible={tooltipVisible}
        position={tooltipPosition}
        onClose={handleCloseTooltip}
      />
    </div>
  );
};

ScatterPlot.propTypes = {
  className: PropTypes.string
};

export default ScatterPlot;
